import { useEffect, useState } from 'react';
import { listItems } from '../fileBrowser/fileBrowserApi';
import { displayNameFromPath } from './mediaFileTypes';

export interface MediaFileMetadata {
  size: number | null;
  dateModified: string | null;
}

function parentDirectoryPath(relativePath: string): string | undefined {
  const normalized = relativePath.replace(/\\/g, '/').replace(/^\/+/, '');
  const slash = normalized.lastIndexOf('/');
  if (slash < 0) return undefined;
  const parent = normalized.slice(0, slash);
  return parent.length > 0 ? parent : undefined;
}

function normalizeRelativePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '');
}

export async function fetchMediaFileMetadata(
  mountId: string,
  relativePath: string
): Promise<MediaFileMetadata | null> {
  if (!mountId || !relativePath) return null;

  const fileName = displayNameFromPath(relativePath);
  const parent = parentDirectoryPath(relativePath);
  const targetPath = normalizeRelativePath(relativePath);

  try {
    const items = await listItems(mountId, parent);
    const match = items.find((item) => {
      if (item.isDirectory) return false;
      const itemPath = normalizeRelativePath(item.path);
      return item.name === fileName || itemPath === targetPath;
    });
    if (!match) return null;
    return { size: match.size, dateModified: match.dateModified };
  } catch {
    return null;
  }
}

export function useMediaFileMetadata(
  mountId: string,
  relativePath: string,
  enabled: boolean
): MediaFileMetadata | null {
  const [metadata, setMetadata] = useState<MediaFileMetadata | null>(null);

  useEffect(() => {
    if (!enabled || !mountId || !relativePath) {
      setMetadata(null);
      return;
    }

    let cancelled = false;
    setMetadata(null);

    void fetchMediaFileMetadata(mountId, relativePath).then((result) => {
      if (!cancelled) setMetadata(result);
    });

    return () => {
      cancelled = true;
    };
  }, [mountId, relativePath, enabled]);

  return metadata;
}
